import { useEffect, useState } from "react";
import { post } from "../js/post";
import "../css/OptometryScreen.css";
import "../css/Glasses.css";
import moment from "moment";
import TooltipBox from "./Tooltip";
import {
  inputOnEnter,
  userData,
  areObjectsEqual,
  focusElement,
  contactLensesTestStructure,
  selectedOptic,
} from "../js/utils";
import { useObservations } from "../hooks/useObservations";

import InputData from "./InputData";
import InputDataControl from "../support-extra/components/InputDataControl";
import InputIconSearch from "./InputIconSearch";
import DropdownFilter from "./DropdownFilter";
import Dropdown from "./Dropdown";
import ButtonIconV from "./ButtonIconV";
import ButtonIconH from "./ButtonIconH";
import SummarySectionBox from "./SummarySectionBox";
import ActionPopup from "./ActionPopup";
import ObservationsPanel from "./ObservationsPanel";
import AssociateProduct from "./AssociateProduct";
import InputTextArea from "./InputTextArea";
import LoadingScreen from "./LoadingScreen";
import PrescriptionList from "./PrescriptionList";

export default function ContactLensesTest({
  id,
  contactLensesId,
  onClose,
  agentList,
  patientInfo,
  createPopupQuestion,
  removePopupQuestion,
  onReloadCLTest,
}) {
  const [actionPopup, setActionPopup] = useState(null);
  const [dateHistoryList, setDateHistoryList] = useState([]);
  const [selectedDate, setSelectedDate] = useState({ id: "", text: "" });
  const [testId, setTestId] = useState(id);
  const [currentCL, setCurrentCL] = useState(contactLensesTestStructure);
  const [currentCLEdit, setCurrentCLEdit] = useState(
    contactLensesTestStructure,
  );
  const [actionComponent, setActionComponent] = useState(null);
  const [toggleObservationPanel, setToggleObservationPanel] = useState(false);
  const { list: observations, refresh: refreshObservations } = useObservations(
    currentCLEdit.idFichaLentillasPruebas,
    "FichaPruebaLC",
  );
  const [copyCLData, setCopyCLData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedPrescriptionEye, setSelectedPrescriptionEye] = useState(null);

  const getCLHistory = async () => {
    const response = await post(
      "/support-extra/fichas/ListarFichaLentillasPruebas.php",
      {
        token: userData.token,
        idFichaLentillas: contactLensesId,
        todas: 1,
      },
    );
    if (response.length > 0) {
      const found = response.find((e) => {
        return e.idFichaLentillasPruebas === testId;
      });
      if (found) {
        setSelectedDate({
          id: found.idFichaLentillasPruebas,
          text: found.fecha,
        });
        setTestId(found.idFichaLentillasPruebas);
      }
    }
    setDateHistoryList(response);
  };

  const getContactLensesTest = async (id) => {
    const response = await post(
      "/support-extra/fichas/ObtenerFichaLentillasPruebas.php",
      {
        token: userData.token,
        idFichaLentillasPruebas: id,
      },
    );

    setCurrentCL(response);
    setCurrentCLEdit(response);
  };

  function showNotProductFoundMessage(code, index) {
    createPopupQuestion(
      `No existe ningún producto con el código ${code}.`,
      () => {
        removePopupQuestion();
        focusElement(index);
      },
      null,
      "Entendido",
    );
  }

  const searchCLByCode = async (side, code) => {
    const lensKey = side === 0 ? "datosLentillaOD" : "datosLentillaOI";
    const codigo = code || currentCLEdit[lensKey].codigo;

    if (!codigo) return;

    const CLResponse = await fetchContactLensData(codigo);

    if (!CLResponse?.id) {
      const index = side === 0 ? 161 : 171;
      showNotProductFoundMessage(codigo, index);
      return;
    }

    const nextIndex = side === 0 ? 162 : 172;
    focusElement(nextIndex);

    const updatedLensData = mapCLData(currentCLEdit[lensKey], CLResponse);
    setCurrentCLEdit((prev) => ({
      ...prev,
      [lensKey]: updatedLensData,
    }));
  };

  const fetchContactLensData = async (codigo) => {
    const payload = {
      token: userData.token,
      codigo,
      tipoCodigo: -1,
      idOptica: selectedOptic.id,
    };
    return await post("ObtenerLenteContacto.php", payload);
  };

  const mapCLData = (currentCLData, response) => ({
    ...currentCLData,
    add: response.add,
    cil: response.cil,
    codigo: response.codigoOPTIWIN,
    dia: response.dia,
    eje: response.eje,
    esf: response.esf,
    nombreComercial: response.nombreComercial,
    rad: response.rad1,
    rad2: response.rad2,
  });

  const cloneCLTest = async () => {
    const response = await post(
      "/support-extra/fichas/ClonarFichaLentillasPruebas.php",
      {
        token: userData.token,
        idFichaLentillasPruebas: currentCLEdit.idFichaLentillasPruebas,
      },
    );
    setTestId(parseInt(response.idFichaLentillasPruebas));
  };

  const cancelChanges = async () => {
    if (dateHistoryList.length > 0) {
      // 1. Si estamos creando una ficha, cargamos la última.
      if (testId === -1) {
        setSelectedDate({
          id: dateHistoryList[0].idFichaLentillasPruebas,
          text: dateHistoryList[0].fecha,
        });
        setTestId(dateHistoryList[0].idFichaLentillasPruebas);
        await getContactLensesTest(dateHistoryList[0].idFichaLentillasPruebas);
      } else {
        // 2. Si estamos viendo una ficha, cancelamos los cambios.
        setCurrentCLEdit(currentCL);
      }
    } else {
      setCurrentCLEdit(currentCL);
    }
  };

  const saveCLTest = async () => {
    if (validateData()) {
      const response = await post(
        "/support-extra/fichas/GuardarFichaLentillasPruebas.php",
        {
          ...currentCLEdit,
          token: userData.token,
          idFichaLentillasPruebas: testId,
          idFichaLentillas: contactLensesId,
          fechaActualizacion: moment().format("YYYY-MM-DDTHH:mm:ss"),
          rad2OD: currentCLEdit.datosLentillaOD.rad2,
          rad2OI: currentCLEdit.datosLentillaOI.rad2,
        },
      );

      if (!response.idFichaLentillasPruebas) return;

      setSelectedDate({
        id: response.idFichaLentillasPruebas,
        text: currentCLEdit.fechaCreacion,
      });
      setTestId(response.idFichaLentillasPruebas);
      setCurrentCLEdit({
        ...currentCLEdit,
        fechaActualizacion: moment().format("YYYY-MM-DDTHH:mm:ss"),
        idFichaLentillasPruebas: response.idFichaLentillasPruebas,
      });
      setCurrentCL({
        ...currentCLEdit,
        fechaActualizacion: moment().format("YYYY-MM-DDTHH:mm:ss"),
        idFichaLentillasPruebas: response.idFichaLentillasPruebas,
      });
      createPopupQuestion(
        `Los cambios se han guardado con éxito.`,
        async () => {
          removePopupQuestion();
        },
        null,
        "Entendido",
      );
    } else {
      createPopupQuestion(
        `Por favor, rellene el agente y al menos el nombre comercial de alguna lente antes de guardar la ficha.`,
        async () => {
          removePopupQuestion();
          focusRequiredField();
        },
        null,
        "Entendido",
      );
    }
  };

  function validateData() {
    return (
      currentCLEdit.empleado !== "" &&
      (currentCLEdit.datosLentillaOD.nombreComercial !== "" ||
        currentCLEdit.datosLentillaOI.nombreComercial !== "")
    );
  }

  function focusRequiredField() {
    const fields = [
      { value: currentCLEdit.empleado, elementId: 234 },
      {
        value: currentCLEdit.datosLentillaOD.nombreComercial,
        elementId: 162,
      },
      {
        value: currentCLEdit.datosLentillaOI.nombreComercial,
        elementId: 172,
      },
    ];

    const emptyField = fields.find((field) => field.value === "");
    if (emptyField) {
      focusElement(emptyField.elementId);
    }
  }

  const resetCLTestState = () => {
    setCurrentCL({
      ...contactLensesTestStructure,
      fechaCreacion: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
    setCurrentCLEdit({
      ...contactLensesTestStructure,
      fechaCreacion: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
    setTestId(-1);
    setSelectedDate({
      id: -1,
      text: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
  };

  const deleteCLTest = async () => {
    await post("/support-extra/fichas/EliminarFichaLentillasPruebas.php", {
      token: userData.token,
      idFichaLentillasPruebas: testId,
      idUsuario: userData.id,
    });
  };

  const selectCLTestAfterDeleting = async () => {
    const response = await post(
      "/support-extra/fichas/ListarFichaLentillasPruebas.php",
      {
        token: userData.token,
        idFichaLentillas: contactLensesId,
        todas: 1,
      },
    );
    if (response.length > 0) {
      setSelectedDate({
        id: response[0].idFichaLentillasPruebas,
        text: response[0].fecha,
      });
      setTestId(response[0].idFichaLentillasPruebas);
      setDateHistoryList(response);
      await getContactLensesTest(response[0].idFichaLentillasPruebas);
    } else {
      setSelectedDate({
        id: -1,
        text: moment().format("YYYY-MM-DD HH:mm:ss"),
      });
      setTestId(-1);
      setCurrentCL(contactLensesTestStructure);
      setCurrentCLEdit(contactLensesTestStructure);
    }
  };

  const handleOpenActionPopup = (componentType) => {
    switch (componentType) {
      case "addRightCL":
        setActionComponent({
          name: "Añadir lente de contacto OD",
          component: (
            <AssociateProduct
              famId={2}
              supplier={{ id: -1, text: "" }}
              brand=""
              onSelect={async (e) => {
                await searchCLByCode(0, e);
                setActionPopup(false);
                setActionComponent(null);
              }}
            ></AssociateProduct>
          ),
        });
        break;
      case "addLeftCL":
        setActionComponent({
          name: "Añadir lente de contacto OI",
          component: (
            <AssociateProduct
              famId={2}
              supplier={{ id: -1, text: "" }}
              brand=""
              onSelect={async (e) => {
                await searchCLByCode(1, e);
                setActionPopup(false);
                setActionComponent(null);
              }}
            ></AssociateProduct>
          ),
        });
        break;
      case "loadPrescription":
        setActionComponent({
          name: "Cargar optometría",
          component: (
            <PrescriptionList
              patientId={patientInfo.id}
              onSelect={(e) => {
                setActionPopup(false);
                setActionComponent(null);
                const isOD = selectedPrescriptionEye === 0;
                setCurrentCLEdit((prev) => ({
                  ...prev,
                  RX: {
                    ...prev.RX,
                    ...(isOD
                      ? {
                          esferaOD: e.esfera_d,
                          cilindroOD: e.cilindro_d,
                          ejeOD: e.eje_d,
                          addOD: e.cerca_d,
                        }
                      : {
                          esferaOI: e.esfera_i,
                          cilindroOI: e.cilindro_i,
                          ejeOI: e.eje_i,
                          addOI: e.cerca_i,
                        }),
                  },
                  AVCC2: {
                    ...prev.AVCC2,
                    ...(isOD
                      ? {
                          VLOD: e.av_cc_lejos_d,
                          VPOD: e.av_cc_cerca_d,
                        }
                      : {
                          VLOI: e.av_cc_lejos_i,
                          VPOI: e.av_cc_cerca_i,
                        }),
                    VLAO: e.av_cc_lejos,
                    VPAO: e.av_cc_cerca,
                  },
                }));
                setSelectedPrescriptionEye(null);
              }}
            ></PrescriptionList>
          ),
        });
        break;
      default:
        setActionComponent(null);
    }
    setActionPopup(true);
  };

  function handleCopyCLData() {
    switch (copyCLData) {
      case 0:
        setCurrentCLEdit({
          ...currentCLEdit,
          datosLentillaOI: {
            ...currentCLEdit.datosLentillaOD,
          },
        });
        break;
      case 1:
        setCurrentCLEdit({
          ...currentCLEdit,
          datosLentillaOD: {
            ...currentCLEdit.datosLentillaOI,
          },
        });
        break;
      default:
        break;
    }
  }

  function checkSaved() {
    createPopupQuestion(
      `Por favor, guarde la ficha de pruebas primero.`,
      () => {
        removePopupQuestion();
      },
      null,
      "Entendido",
    );
  }

  useEffect(() => {
    async function initData() {
      focusElement(161);
      getCLHistory();
      if (testId !== -1) {
        getContactLensesTest(testId);
      } else {
        setSelectedDate({
          id: -1,
          text: moment().format("YYYY-MM-DD HH:mm:ss"),
        });
        setCurrentCLEdit({
          ...currentCLEdit,
          fechaCreacion: moment().format("YYYY-MM-DD HH:mm:ss"),
          idFichaLentillas: -1,
          idFichaLentillasPruebas: -1,
          empleado: userData.nombre + " " + userData.apellidos,
        });
        setCurrentCL({
          ...currentCLEdit,
          fechaCreacion: moment().format("YYYY-MM-DD HH:mm:ss"),
          idFichaLentillas: -1,
          idFichaLentillasPruebas: -1,
          empleado: userData.nombre + " " + userData.apellidos,
        });
      }
      setLoadingData(false);
    }
    initData();
  }, [testId]);

  useEffect(() => {
    if (actionComponent !== null) {
      setActionPopup(true);
    }
  }, [actionComponent]);

  useEffect(() => {
    handleCopyCLData();
  }, [copyCLData]);

  useEffect(() => {
    if (selectedPrescriptionEye !== null)
      handleOpenActionPopup("loadPrescription");
  }, [selectedPrescriptionEye]);

  return (
    <div className="popup-container">
      {actionPopup && (
        <ActionPopup
          name={actionComponent.name}
          children={actionComponent.component}
          onClose={() => {
            setActionPopup(false);
            setActionComponent(null);
            setSelectedPrescriptionEye(null);
          }}
        ></ActionPopup>
      )}
      {toggleObservationPanel && (
        <ObservationsPanel
          objectInfo={currentCLEdit.idFichaLentillasPruebas}
          category="FichaPruebaLC"
          onClose={async () => {
            setToggleObservationPanel(false);
            refreshObservations();
          }}
        ></ObservationsPanel>
      )}
      <div className="popup-screen">
        <div className="popup-screen-header">
          <button
            className="popup-action-icon close-icon"
            onClick={async () => {
              if (!areObjectsEqual(currentCL, currentCLEdit)) {
                createPopupQuestion(
                  `¿Desea guardar los cambios realizados antes de salir?`,
                  async () => {
                    await saveCLTest();
                    removePopupQuestion();
                    onClose();
                  },
                  async () => {
                    removePopupQuestion();
                    onClose();
                  },
                  "Sí",
                  "No",
                );
              } else {
                onClose();
              }
            }}
          >
            <img src="/images/close-white.svg" loading="lazy" alt="Icon"></img>
          </button>
          <span className="external-window-header-title">
            Ficha de pruebas de lentes de contacto
          </span>
        </div>
        {loadingData && (
          <LoadingScreen message="Cargando ficha de pruebas de lentes de contacto..."></LoadingScreen>
        )}
        {!loadingData && (
          <div className="tab-content">
            <div className="popup-screen-actions">
              <div className="popup-screen-box">
                <div className="popup-screen-box-info">
                  <div className="cell-extra">
                    <span className="label">Creación</span>
                  </div>
                  <div className="cell-extra w-220">
                    <div className="input-data input-full">
                      <input
                        type="datetime-local"
                        max="9999-12-31T23:59"
                        step="1"
                        tabIndex={232}
                        className="input-field input-cell"
                        value={currentCLEdit.fechaCreacion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            fechaCreacion: e.target.value,
                          });
                        }}
                        onKeyUp={(e) => inputOnEnter(e)}
                      ></input>
                    </div>
                  </div>
                  <div className="cell-extra">
                    <span className="label">Actualización</span>
                  </div>
                  <div className="cell-extra w-220">
                    <InputData
                      size={0}
                      input={
                        currentCLEdit.fechaActualizacion
                          ? moment(currentCLEdit.fechaActualizacion).format(
                              "DD/MM/YYYY HH:mm:ss",
                            )
                          : ""
                      }
                      inactive={true}
                    ></InputData>
                  </div>
                  <div className="cell-extra">
                    <span className="label">
                      Histórico ({dateHistoryList.length})
                    </span>
                  </div>
                  <div className="cell-extra w-100p">
                    <DropdownFilter
                      tabIndex={233}
                      selectedOptions={[]}
                      optionList={dateHistoryList.map((item) => ({
                        id: item.idFichaLentillasPruebas,
                        text: moment(item.fecha, "YYYY-MM-DD HH:mm:ss").format(
                          "DD/MM/YYYY HH:mm:ss",
                        ),
                        active:
                          selectedDate.id === item.idFichaLentillasPruebas,
                      }))}
                      size={0}
                      op={moment(
                        selectedDate.text,
                        "YYYY-MM-DD HH:mm:ss",
                      ).format("DD/MM/YYYY HH:mm:ss")}
                      disabled={testId === -1}
                      onChange={async (e) => {
                        setLoadingData(true);
                        setCurrentCLEdit(contactLensesTestStructure);
                        setSelectedDate({
                          id: e.id,
                          text: moment(e.text, "DD/MM/YYYY HH:mm:ss").format(
                            "YYYY-MM-DD HH:mm:ss",
                          ),
                        });
                        setTestId(e.id);
                      }}
                    ></DropdownFilter>
                  </div>
                </div>
              </div>
              <div className="popup-screen-box">
                <ButtonIconV
                  text="Nuevo"
                  icon={
                    testId !== -1
                      ? "/images/create-blue.svg"
                      : "/images/create-gray.svg"
                  }
                  onClick={() => {
                    if (testId !== -1) {
                      if (!areObjectsEqual(currentCL, currentCLEdit)) {
                        createPopupQuestion(
                          `Existen cambios sin guardar en la ficha de pruebas actual. ¿Desea guardar los cambios antes de crear una nueva ficha de pruebas?`,
                          async () => {
                            await saveCLTest();
                            removePopupQuestion();
                            resetCLTestState();
                            onReloadCLTest();
                          },
                          async () => {
                            removePopupQuestion();
                            resetCLTestState();
                          },
                          "Sí",
                          "No",
                        );
                      } else {
                        resetCLTestState();
                      }
                    }
                  }}
                ></ButtonIconV>
                <ButtonIconV
                  text="Reposición"
                  icon={
                    testId !== -1
                      ? "/images/clone-blue.svg"
                      : "/images/clone-gray.svg"
                  }
                  onClick={() => {
                    if (testId !== -1) {
                      createPopupQuestion(
                        `¿Desea realizar reposición de esta ficha de pruebas de lentes de contacto?`,
                        async () => {
                          await cloneCLTest();
                          onReloadCLTest();
                          removePopupQuestion();
                        },
                        async () => {
                          removePopupQuestion();
                        },
                        "Sí",
                        "No",
                      );
                    }
                  }}
                ></ButtonIconV>
                <ButtonIconV
                  text="Guardar"
                  icon="/images/save-blue.svg"
                  onClick={async () => {
                    await saveCLTest();
                    await getCLHistory();
                  }}
                ></ButtonIconV>
                <ButtonIconV
                  text="Cancelar"
                  icon="/images/cancel-blue.svg"
                  onClick={() => {
                    if (
                      dateHistoryList.length === 0 &&
                      areObjectsEqual(currentCL, currentCLEdit)
                    ) {
                      removePopupQuestion();
                    } else if (
                      !areObjectsEqual(currentCL, currentCLEdit) ||
                      testId === -1
                    ) {
                      createPopupQuestion(
                        `¿Desea cancelar los cambios realizados?`,
                        async () => {
                          await cancelChanges();
                          removePopupQuestion();
                        },
                        async () => {
                          removePopupQuestion();
                        },
                        "Sí",
                        "No",
                      );
                    }
                  }}
                ></ButtonIconV>
                <ButtonIconV
                  text="Recargar"
                  icon={
                    contactLensesId !== -1
                      ? "/images/update-blue.svg"
                      : "/images/update-gray.svg"
                  }
                  onClick={() => {
                    if (testId !== -1) {
                      createPopupQuestion(
                        `¿Desea recargar esta ficha de pruebas de lentes de contacto?`,
                        async () => {
                          await getContactLensesTest(testId);
                          removePopupQuestion();
                        },
                        async () => {
                          removePopupQuestion();
                        },
                        "Sí",
                        "No",
                      );
                    }
                  }}
                ></ButtonIconV>
                <ButtonIconV
                  text="Eliminar"
                  icon={
                    testId !== -1
                      ? "/images/remove-blue.svg"
                      : "/images/remove-gray.svg"
                  }
                  onClick={async () => {
                    if (testId !== -1) {
                      createPopupQuestion(
                        `¿Desea eliminar esta ficha de pruebas de lentes de contacto?`,
                        async () => {
                          await deleteCLTest();
                          await getCLHistory();
                          await selectCLTestAfterDeleting();
                          onReloadCLTest();
                          removePopupQuestion();
                        },
                        async () => {
                          removePopupQuestion();
                        },
                        "Sí",
                        "No",
                      );
                    }
                  }}
                ></ButtonIconV>
                <ButtonIconV
                  text="Imprimir"
                  icon={
                    testId !== -1
                      ? "/images/print-blue.svg"
                      : "/images/print-gray.svg"
                  }
                  onClick={() => {}}
                ></ButtonIconV>
              </div>
            </div>
            <div className="sep-line"></div>
            <div className="opt-table-container">
              <div className="popup-screen-content" style={{ gap: 40 }}>
                <div className="popup-screen-content-col w-50">
                  <div className="opt-table-title">
                    Datos lente de contacto OD
                  </div>
                  <div
                    className="opt-table-content grid-7"
                    id="cl-test-content-grid"
                  >
                    <div className="cell cell-label cell-start-content">
                      Código
                    </div>
                    <div className="cell cell-start-content combined-cells-6">
                      <InputIconSearch
                        tabIndex={161}
                        type="type-only-numbers"
                        size={6}
                        maxLength={10}
                        input={currentCLEdit.datosLentillaOD.codigo}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              codigo: e,
                            },
                          });
                        }}
                        onClear={() => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              codigo: "",
                            },
                          });
                        }}
                        onEnter={async () => {
                          await searchCLByCode(
                            0,
                            currentCLEdit.datosLentillaOD.codigo,
                          );
                        }}
                      ></InputIconSearch>
                      <ButtonIconH
                        text="Añadir"
                        small
                        onClick={() => {
                          handleOpenActionPopup("addRightCL");
                        }}
                      ></ButtonIconH>
                      <ButtonIconH
                        text="Tarifa"
                        small
                        onClick={() => {}}
                      ></ButtonIconH>
                      <button
                        className="cell-icon"
                        onClick={() => {
                          setCopyCLData(0);
                        }}
                      >
                        <img
                          src="/images/extract-blue.svg"
                          loading="lazy"
                          alt="Icon"
                          className="cell-icon"
                          data-tooltip-id="tt1"
                        ></img>
                        <TooltipBox
                          id="tt1"
                          content={<div className="cell">Copiar a LCI</div>}
                        ></TooltipBox>
                      </button>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Nombre comercial
                    </div>
                    <div className="cell combined-cells-6">
                      <InputData
                        tabIndex={162}
                        maxLength={40}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.datosLentillaOD.nombreComercial}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              nombreComercial: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label">Esf</div>
                    <div className="cell cell-label">Cil</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell cell-label">Add</div>
                    <div className="cell cell-label">Rad</div>
                    <div className="cell cell-label">Rad2</div>
                    <div className="cell cell-label">Diá</div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={163}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.datosLentillaOD.esf}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              esf: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={164}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.datosLentillaOD.cil}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              cil: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={165}
                        type="type-only-positive-numbers"
                        size={0}
                        maxLength={3}
                        input={currentCLEdit.datosLentillaOD.eje}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              eje: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={166}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.datosLentillaOD.add}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              add: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={167}
                        maxLength={6}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.datosLentillaOD.rad}
                        customStyle="content-center"
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              rad: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={168}
                        maxLength={6}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.datosLentillaOD.rad2}
                        customStyle="content-center"
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              rad2: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={169}
                        type="type-float2"
                        size={0}
                        input={currentCLEdit.datosLentillaOD.dia}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              dia: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content combined-cells-7">
                      Observaciones
                    </div>
                    <div className="cell combined-cells-7">
                      <InputTextArea
                        tabIndex={170}
                        type="type-full"
                        size={0}
                        maxLength={100}
                        input={currentCLEdit.datosLentillaOD.observaciones}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOD: {
                              ...currentCLEdit.datosLentillaOD,
                              observaciones: e,
                            },
                          });
                        }}
                        extra="small-height"
                      ></InputTextArea>
                    </div>
                    <div className="cell combined-cells-7"></div>
                  </div>
                  <div className="opt-table-title">
                    Datos lente de contacto OI
                  </div>
                  <div
                    className="opt-table-content grid-7"
                    id="cl-test-content-grid"
                  >
                    <div className="cell cell-label cell-start-content">
                      Código
                    </div>
                    <div className="cell cell-start-content combined-cells-6">
                      <InputIconSearch
                        tabIndex={171}
                        type="type-only-numbers"
                        size={6}
                        maxLength={10}
                        input={currentCLEdit.datosLentillaOI.codigo}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              codigo: e,
                            },
                          });
                        }}
                        onClear={() => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              codigo: "",
                            },
                          });
                        }}
                        onEnter={async () => {
                          await searchCLByCode(
                            1,
                            currentCLEdit.datosLentillaOI.codigo,
                          );
                        }}
                      ></InputIconSearch>
                      <ButtonIconH
                        text="Añadir"
                        small
                        onClick={() => {
                          handleOpenActionPopup("addLeftCL");
                        }}
                      ></ButtonIconH>
                      <ButtonIconH
                        text="Tarifa"
                        small
                        onClick={() => {}}
                      ></ButtonIconH>
                      <button
                        className="cell-icon"
                        onClick={() => {
                          setCopyCLData(1);
                        }}
                      >
                        <img
                          src="/images/extract-up-blue.svg"
                          loading="lazy"
                          alt="Icon"
                          className="cell-icon"
                          data-tooltip-id="tt2"
                        ></img>
                        <TooltipBox
                          id="tt2"
                          content={<div className="cell">Copiar a LCD</div>}
                        ></TooltipBox>
                      </button>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Nombre comercial
                    </div>
                    <div className="cell combined-cells-6">
                      <InputData
                        tabIndex={172}
                        maxLength={40}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.datosLentillaOI.nombreComercial}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              nombreComercial: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label">Esf</div>
                    <div className="cell cell-label">Cil</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell cell-label">Add</div>
                    <div className="cell cell-label">Rad</div>
                    <div className="cell cell-label">Rad2</div>
                    <div className="cell cell-label">Diá</div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={173}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.datosLentillaOI.esf}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              esf: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={174}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.datosLentillaOI.cil}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              cil: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={175}
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentCLEdit.datosLentillaOI.eje}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              eje: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={176}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.datosLentillaOI.add}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              add: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={177}
                        maxLength={6}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.datosLentillaOI.rad}
                        customStyle="content-center"
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              rad: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={178}
                        maxLength={6}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.datosLentillaOI.rad2}
                        customStyle="content-center"
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              rad2: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={179}
                        type="type-float2"
                        size={0}
                        input={currentCLEdit.datosLentillaOI.dia}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              dia: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content combined-cells-7">
                      Observaciones
                    </div>
                    <div className="cell combined-cells-7">
                      <InputTextArea
                        tabIndex={180}
                        type="type-full"
                        size={0}
                        maxLength={100}
                        input={currentCLEdit.datosLentillaOI.observaciones}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            datosLentillaOI: {
                              ...currentCLEdit.datosLentillaOI,
                              observaciones: e,
                            },
                          });
                        }}
                        extra="small-height"
                      ></InputTextArea>
                    </div>
                    <div className="cell combined-cells-7"></div>
                  </div>
                  <div className="opt-table-title">Otros</div>
                  <div className="opt-table-content grid-5">
                    <div className="cell"></div>
                    <div className="cell combined-cells-2 opt-table-subtitle">
                      OD
                    </div>
                    <div className="cell combined-cells-2 opt-table-subtitle">
                      OI
                    </div>

                    <div className="cell cell-label cell-start-content">
                      Movimiento
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={181}
                        maxLength={60}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.movimientoOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              movimientoOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={187}
                        maxLength={60}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.movimientoOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              movimientoOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Centrado
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={182}
                        maxLength={10}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.centradoOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              centradoOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={188}
                        maxLength={10}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.centradoOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              centradoOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Giros/Tipo
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={183}
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentCLEdit.otros.girosOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              girosOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={184}
                        maxLength={10}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.tipoGiroOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              tipoGiroOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={189}
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentCLEdit.otros.girosOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              girosOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={190}
                        maxLength={10}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.tipoGirosOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              tipoGirosOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Fluorograma
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={185}
                        maxLength={10}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.fluorogramaOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              fluorogramaOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={191}
                        maxLength={10}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.fluorogramaOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              fluorogramaOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Comodidad
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={186}
                        maxLength={60}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.comodidadOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              comodidadOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={192}
                        maxLength={60}
                        type="type-alphanum-ext"
                        size={0}
                        input={currentCLEdit.otros.comodidadOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            otros: {
                              ...currentCLEdit.otros,
                              comodidadOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                  </div>
                </div>
                <div className="popup-screen-content-col w-50">
                  <div className="opt-table-content grid-9" id="cl-test-t0">
                    <div className="cell cell-label">Agente (*)</div>
                    <div className="cell combined-cells-3">
                      <Dropdown
                        tabIndex={234}
                        optionList={agentList}
                        size={0}
                        op={currentCLEdit.empleado}
                        mandatory={false}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            empleado: e.text,
                          });
                        }}
                      ></Dropdown>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label">Horas de uso</div>
                    <div className="cell combined-cells-3">
                      <InputData
                        tabIndex={193}
                        type="type-float2"
                        size={0}
                        input={currentCLEdit.horasUso}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            horasUso: e,
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-9"></div>
                  </div>
                  <div className="opt-table-content grid-9" id="cl-test-t1">
                    <div className="cell"></div>
                    <div className="cell combined-cells-2 opt-table-subtitle">
                      AVSC
                    </div>
                    <div className="cell"></div>
                    <div className="cell combined-cells-2 opt-table-subtitle">
                      AVCC
                    </div>
                    <div className="cell"></div>
                    <div className="cell combined-cells-2 opt-table-subtitle">
                      AVSRX
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label">VL</div>
                    <div className="cell cell-label">VP</div>
                    <div className="cell"></div>
                    <div className="cell cell-label">VL</div>
                    <div className="cell cell-label">VP</div>
                    <div className="cell"></div>
                    <div className="cell cell-label">VL</div>
                    <div className="cell cell-label">VP</div>
                    <div className="cell cell-label">OD</div>
                    <div className="cell">
                      <InputData
                        tabIndex={194}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSC.VLOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSC: {
                              ...currentCLEdit.AVSC,
                              VLOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={195}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSC.VPOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSC: {
                              ...currentCLEdit.AVSC,
                              VPOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell">
                      <InputData
                        tabIndex={196}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC.VLOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC: {
                              ...currentCLEdit.AVCC,
                              VLOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={197}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC.VPOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC: {
                              ...currentCLEdit.AVCC,
                              VPOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell">
                      <InputData
                        tabIndex={198}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSRX.VLOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSRX: {
                              ...currentCLEdit.AVSRX,
                              VLOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={199}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSRX.VPOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSRX: {
                              ...currentCLEdit.AVSRX,
                              VPOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label">OI</div>
                    <div className="cell">
                      <InputData
                        tabIndex={200}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSC.VLOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSC: {
                              ...currentCLEdit.AVSC,
                              VLOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={201}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSC.VPOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSC: {
                              ...currentCLEdit.AVSC,
                              VPOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell">
                      <InputData
                        tabIndex={202}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC.VLOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC: {
                              ...currentCLEdit.AVCC,
                              VLOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={203}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC.VPOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC: {
                              ...currentCLEdit.AVCC,
                              VPOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell">
                      <InputData
                        tabIndex={204}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSRX.VLOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSRX: {
                              ...currentCLEdit.AVSRX,
                              VLOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={205}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSRX.VPOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSRX: {
                              ...currentCLEdit.AVSRX,
                              VPOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label">AO</div>
                    <div className="cell">
                      <InputData
                        tabIndex={206}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSC.VLAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSC: {
                              ...currentCLEdit.AVSC,
                              VLAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={207}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSC.VPAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSC: {
                              ...currentCLEdit.AVSC,
                              VPAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell">
                      <InputData
                        tabIndex={208}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC.VLAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC: {
                              ...currentCLEdit.AVCC,
                              VLAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={209}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC.VPAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC: {
                              ...currentCLEdit.AVCC,
                              VPAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell">
                      <InputData
                        tabIndex={210}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSRX.VLAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSRX: {
                              ...currentCLEdit.AVSRX,
                              VLAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={211}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVSRX.VPAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVSRX: {
                              ...currentCLEdit.AVSRX,
                              VPAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-9"></div>
                  </div>
                  <div className="opt-table-content grid-8" id="cl-test-t2">
                    <div className="cell"></div>
                    <div className="cell combined-cells-4 opt-table-subtitle">
                      RX
                    </div>
                    <div className="cell"></div>
                    <div className="cell combined-cells-2 opt-table-subtitle">
                      AVCC
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label">Esfera</div>
                    <div className="cell cell-label">Cilindro</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell cell-label">Add</div>
                    <div className="cell"></div>
                    <div className="cell cell-label">VL</div>
                    <div className="cell cell-label">VP</div>
                    <div className="cell cell-label">OD</div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={212}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.RX.esferaOD}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              esferaOD: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={213}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.RX.cilindroOD}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              cilindroOD: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={214}
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentCLEdit.RX.ejeOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              ejeOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={215}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.RX.addOD}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              addOD: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <button
                        className="cell-icon"
                        onClick={() => {
                          setSelectedPrescriptionEye(0);
                        }}
                      >
                        <img
                          src="/images/left-blue.svg"
                          loading="lazy"
                          alt="Icon"
                          className="cell-icon"
                          data-tooltip-id="tt3"
                        ></img>
                        <TooltipBox
                          id="tt3"
                          content={
                            <div className="cell">Cargar graduación</div>
                          }
                        ></TooltipBox>
                      </button>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={216}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC2.VLOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC2: {
                              ...currentCLEdit.AVCC2,
                              VLOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={217}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC2.VPOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC2: {
                              ...currentCLEdit.AVCC2,
                              VPOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label">OI</div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={218}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.RX.esferaOI}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              esferaOI: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={219}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.RX.cilindroOI}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              cilindroOI: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={220}
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentCLEdit.RX.ejeOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              ejeOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={221}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.RX.addOI}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            RX: {
                              ...currentCLEdit.RX,
                              addOI: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <button
                        className="cell-icon"
                        onClick={() => {
                          setSelectedPrescriptionEye(1);
                        }}
                      >
                        <img
                          src="/images/left-blue.svg"
                          loading="lazy"
                          alt="Icon"
                          className="cell-icon"
                          data-tooltip-id="tt4"
                        ></img>
                        <TooltipBox
                          id="tt4"
                          content={
                            <div className="cell">Cargar graduación</div>
                          }
                        ></TooltipBox>
                      </button>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={222}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC2.VLOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC2: {
                              ...currentCLEdit.AVCC2,
                              VLOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={223}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC2.VPOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC2: {
                              ...currentCLEdit.AVCC2,
                              VPOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-6"></div>
                    <div className="cell">
                      <InputData
                        tabIndex={224}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC2.VLAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC2: {
                              ...currentCLEdit.AVCC2,
                              VLAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={225}
                        type="type-alphanum-ext"
                        size={0}
                        maxLength={9}
                        customStyle="content-center"
                        input={currentCLEdit.AVCC2.VPAO}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            AVCC2: {
                              ...currentCLEdit.AVCC2,
                              VPAO: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                  </div>
                  <div className="opt-table-content grid-8" id="cl-test-t2">
                    <div className="cell"></div>
                    <div className="cell combined-cells-3 opt-table-subtitle">
                      SRX
                    </div>
                    <div className="cell combined-cells-4"></div>
                    <div className="cell"></div>
                    <div className="cell cell-label">Esfera</div>
                    <div className="cell cell-label">Cilindro</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell combined-cells-4"></div>
                    <div className="cell cell-label">OD</div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={226}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.SRX.esferaOD}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            SRX: {
                              ...currentCLEdit.SRX,
                              esferaOD: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={227}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.SRX.cilindroOD}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            SRX: {
                              ...currentCLEdit.SRX,
                              cilindroOD: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={228}
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentCLEdit.SRX.ejeOD}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            SRX: {
                              ...currentCLEdit.SRX,
                              ejeOD: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-4"></div>
                    <div className="cell cell-label">OI</div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={229}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.SRX.esferaOI}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            SRX: {
                              ...currentCLEdit.SRX,
                              esferaOI: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={230}
                        type="type-optofloat"
                        size={0}
                        input={currentCLEdit.SRX.cilindroOI}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={createPopupQuestion}
                        removePopupQuestion={removePopupQuestion}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            SRX: {
                              ...currentCLEdit.SRX,
                              cilindroOI: e,
                            },
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={231}
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentCLEdit.SRX.ejeOI}
                        onChange={(e) => {
                          setCurrentCLEdit({
                            ...currentCLEdit,
                            SRX: {
                              ...currentCLEdit.SRX,
                              ejeOI: e,
                            },
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell combined-cells-4"></div>
                    <div className="cell combined-cells-8"></div>
                  </div>
                  <SummarySectionBox
                    name="Observaciones"
                    numElements={observations.length}
                    onOpen={() => {
                      if (testId !== -1) {
                        setToggleObservationPanel(true);
                      } else {
                        checkSaved();
                      }
                    }}
                  ></SummarySectionBox>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
